import React, { useState, useContext, useEffect, useRef } from 'react'
import { GlobalContext, AuthContext } from '../../services/context'
import { loginInfo } from '../../services/authConfig'
import {
  getHighestRole,
  getAssignments,
  getUserProfileByCongitoUsername
} from '../../services/apiHelper'
import Logo from '../../assets/images/GrowMore360_Logo.svg'
import { Login, Modal, Button } from 'syngenta-react-components'
import jwt from 'jsonwebtoken'

const LoginView = () => {
  const { dispatch, trackEvent } = useContext(GlobalContext)
  const { authDispatch } = useContext(AuthContext)
  const [isLoading, setIsLoading] = useState(false)
  const [showSSOError, setShowSSOError] = useState(false)
  const [triggerTextCheck, setTriggerTextCheck] = useState(false)

  // check if the text is present on dom or not
  const checkForText = () => {
    const searchText = 'Email/Phone'
    const bodyText = document.body.textContent || document.body.innerText
    if (bodyText.includes(searchText)) {
      setTriggerTextCheck(true)
    } else {
      setTriggerTextCheck(false)
    }
  }
  
  useEffect(() => {
    // Set up a MutationObserver to monitor changes in the DOM
    const observer = new MutationObserver(() => {
      checkForText()
    })
    // Start observing the body
    observer.observe(document.body, {
      childList: true,  
      subtree: true,  
      characterData: true 
    })
    checkForText()
    // disconnect observer in Unmount phase for uneccessary rendering
    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    const phoneElement = document.getElementById('phoneOrEmail')
    const setInputToLowercase = (element, value) => {
      if (!element) return
      const lowercasedValue = value.toLowerCase()
      if (element && element.value !== lowercasedValue) {
        const start = element.selectionStart
        const end = element.selectionEnd
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value'
        ).set
        nativeInputValueSetter.call(element, lowercasedValue)
        const inputEvent = new Event('input', { bubbles: true })
        element.dispatchEvent(inputEvent)
        // Restore the cursor position
        element.setSelectionRange(start, end)
      }
    }
    const handleInput = event => {
      const value = event.target.value
      if(value !== '') {
        setInputToLowercase(phoneElement, value)
      }
    }
    if (phoneElement) {
      phoneElement.addEventListener('input', handleInput)
      setInputToLowercase(phoneElement, phoneElement.value)
      // Clean up event listeners when the component unmounts
      return () => {
        phoneElement.removeEventListener('input', handleInput)
      }
    }
  }, [triggerTextCheck])
  
  const cognitoHandler = async response => {
    if (response?.user?.username) {
      setIsLoading(true)
      trackEvent('successful_login', response.user)
      authDispatch({ type: 'LOGIN_USER', payload: response.user })
      dispatch({ type: 'GO_TO_DASHBOARD' })
      const highestRole = await getHighestRole()
      if (highestRole) {
        authDispatch({ type: 'SET_HIGHEST_ROLE', payload: highestRole.role_type })
        authDispatch({ type: 'UPDATE_USER_PROFILE', payload: highestRole })
        setIsLoading(false)
        getAssignments(highestRole.user_id, 'user_id')
          .then(res => {
            if(res){
              const assignmentsObj = {}
              res.forEach(company => {
                assignmentsObj[company.company_id] = company.permissions
              })
              authDispatch({ type: 'UPDATE_USER_PERMISSIONS', payload: assignmentsObj })
            } else {
              authDispatch({ type: 'UPDATE_USER_PERMISSIONS_FAILED' })
            }
          })
      } else {
        setIsLoading(false)
      }
    } else if (response?.accessToken) {
      setIsLoading(true)
      let decoded = jwt.decode(response.accessToken)
      getUserProfileByCongitoUsername(decoded.username, response.accessToken)
        .then(async res => {
          if (res) {
            res.cognito_username = decoded.username
            getAssignments(res.user_id, 'user_id')
              .then(res => {
                if(res){
                  const assignmentsObj = {}
                  res.forEach(company => {
                    assignmentsObj[company.company_id] = company.permissions
                  })
                  authDispatch({ type: 'UPDATE_USER_PERMISSIONS', payload: assignmentsObj })
                } else {
                  authDispatch({ type: 'UPDATE_USER_PERMISSIONS_FAILED' })
                }
              })
          }
          trackEvent('successful_login', { response: 'login success' })
          authDispatch({ type: 'LOGIN_USER', payload: res })
          authDispatch({ type: 'UPDATE_USER_PROFILE', payload: res })
          const highestRole = await getHighestRole()
          if (highestRole) {
            authDispatch({ type: 'SET_HIGHEST_ROLE', payload: highestRole.role_type })
          }
          setIsLoading(false)
        })
        .catch(error => {
          console.error(error)
          authDispatch({ type: 'CHECK_FOR_PHONE' })
          setIsLoading(true)
        })
    } else if (response?.error) {
      trackEvent('failed_login', { response: 'login failed' })
      console.error('Error logging in ' + response.error)
      if (window.location.href.includes('#error_description=Error+in+SAML')){
        setShowSSOError(true)
      }
    }
    return response
  }


  return (
    <div className='df fxdrc jcc aic'>
      <Modal show={showSSOError}
        customClassName='h200 df jcc aic'
        handleClose={() => setShowSSOError(false)}>
        <div>
          <div className='mb30'>
            {`Unable to log in. Please contact ${loginInfo.errorContact}`}
          </div>
          <Button styleType='success' onClick={() => {
            setShowSSOError(false)
            dispatch({ type: 'GO_TO_LOGIN' })
          }} text='Close'></Button>
        </div>
      </Modal>
      {/* Had to add key here otherwise it would not update the child when we updated isLoading */}
      <Login
        {...loginInfo}
        logoPath={Logo}
        propsIsLoading={isLoading}
        key={isLoading}
        googleLogin
        facebookLogin
        forgotPasswordPath='/forgot-password'
        handleCheckForAccessToken
        containerClasses='bgc-white bd1-s-darkgray w375 pr25 pl25'
        handler={response => cognitoHandler(response, 'login')}
      />
    </div>
  )
}

export default LoginView
